import { useMemo, useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@antimatterhq/ui/components/collapsible';
import { Icon } from '@antimatterhq/ui/components/icon';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@antimatterhq/ui/components/sidebar';
import { useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { cn } from '@antimatterhq/ui/lib/utils';
import { useClientFrameNavigate } from '@/hooks/navigation/useClientFrameNavigate';
import { useInstanceId } from '@/contexts/InstanceIdContext';

export type SubMenuItem = {
  title: string;
  clientPath: string;
};

export type ClientMenuItemType = {
  title: string;
  clientPath: string;
  basePath: string;
  icon: () => JSX.Element;
  submenu?: SubMenuItem[];
  url?: string;
};

const useClientMenuItems = () => {
  const { enableTransforms, enableQueryPage } = useFlags<FlagSet>();
  const { instanceId } = useInstanceId();

  return useMemo(() => {
    return [
      {
        title: 'Overview',
        clientPath: '/home',
        basePath: '/home',
        icon: () => (
          <Icon icon="lucide:shield-plus" data-testid="icon-overview" />
        ),
      },
      {
        title: 'Configure',
        clientPath: '/configure',
        basePath: '/configure',
        icon: () => <Icon icon="lucide:bolt" data-testid="icon-configure" />,
        submenu: [
          {
            title: 'Datasources',
            clientPath: '/configure/data-sources',
          },
          {
            title: 'Detection rules',
            clientPath: '/configure/detection-rules',
          },
          {
            title: 'Transformations',
            clientPath: '/configure/transforms',
          },
          {
            title: 'Workspace',
            clientPath: '/configure/workspace',
          },
          {
            title: 'Admin',
            clientPath: '/configure/admin',
          },
        ].filter((item) =>
          enableTransforms ? true : item.title !== 'Transformations',
        ),
      },
      {
        title: 'Explore',
        clientPath: '/explore',
        basePath: '/explore',
        icon: () => (
          <Icon icon="antimatterhq:binoculars" data-testid="icon-explore" />
        ),
        submenu: [
          {
            title: 'Notables',
            clientPath: '/explore/notables',
          },
          {
            title: 'Observables',
            clientPath: '/explore/observables',
          },
          {
            title: 'Health',
            clientPath: '/explore/health',
          },
          {
            title: 'Query',
            clientPath: '/explore/query',
          },
        ].filter((item) => (enableQueryPage ? true : item.title !== 'Query')),
      },
    ].map((item) => ({
      ...item,
      url: `${window.location.origin}/${instanceId}${item.clientPath}`,
    }));
  }, [enableQueryPage, enableTransforms, instanceId]);
};

export const StandaloneSidebarClientMenuItemWithSubmenu = ({
  item,
  disabled,
}: {
  item: ClientMenuItemType;
  disabled: boolean;
}) => {
  const { pathname } = useLocation();
  const { instanceId } = useInstanceId();
  const { navigateClient } = useClientFrameNavigate();
  const { open: sidebarOpen } = useSidebar();

  const isActive = pathname.includes(item.basePath);
  const [open, setOpen] = useState(true);

  return (
    <Collapsible
      defaultOpen={true}
      className="group/collapsible"
      onOpenChange={setOpen}
    >
      <SidebarMenuItem data-testid={`menu-item-${item.title.toLowerCase()}`}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isActive}
            disabled={disabled}
            data-testid={`menu-button-${item.title.toLowerCase()}`}
            asChild={!sidebarOpen}
          >
            {!sidebarOpen ? (
              <a
                href={item.url}
                onClick={(e) => {
                  e.preventDefault();
                  navigateClient(item.clientPath);
                }}
                data-testid={`client-link-${item.title.toLowerCase()}`}
              >
                <item.icon />
                <span data-testid={`menu-text-${item.title.toLowerCase()}`}>
                  {item.title}
                </span>
                <Icon
                  icon="lucide:chevron-right"
                  className={cn(
                    'ml-auto h-4 w-4 transition-transform duration-200 ease-in-out',
                    open ? 'rotate-90' : '',
                  )}
                />
              </a>
            ) : (
              <>
                <item.icon />
                <span data-testid={`menu-text-${item.title.toLowerCase()}`}>
                  {item.title}
                </span>
                <Icon
                  icon="lucide:chevron-right"
                  className={cn(
                    'ml-auto h-4 w-4 transition-transform duration-200 ease-in-out',
                    open ? 'rotate-90' : '',
                  )}
                />
              </>
            )}
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub data-testid={`submenu-${item.title.toLowerCase()}`}>
            {item.submenu?.map((subItem) => (
              <SidebarMenuSubItem
                key={subItem.title}
                data-testid={`submenu-item-${subItem.title.toLowerCase()}`}
              >
                <SidebarMenuSubButton
                  asChild
                  disabled={disabled}
                  isActive={!disabled && pathname.includes(subItem.clientPath)}
                  data-testid={`submenu-button-${subItem.title.toLowerCase()}`}
                >
                  <a
                    href={`${window.location.origin}/${instanceId}${subItem.clientPath}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateClient(subItem.clientPath);
                    }}
                    data-testid={`client-link-${subItem.title.toLowerCase()}`}
                  >
                    <span
                      data-testid={`submenu-text-${subItem.title.toLowerCase()}`}
                    >
                      {subItem.title}
                    </span>
                  </a>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

export const StandaloneSidebarClientMenuItem = ({
  item,
  disabled,
}: {
  item: ClientMenuItemType;
  disabled: boolean;
}) => {
  const { pathname } = useLocation();
  const { navigateClient } = useClientFrameNavigate();

  const isActive = !disabled && pathname.includes(item.basePath);

  return (
    <SidebarMenuItem
      key={item.title}
      data-testid={`menu-item-${item.title.toLowerCase()}`}
    >
      <SidebarMenuButton
        asChild
        isActive={isActive}
        disabled={disabled}
        data-testid={`menu-button-${item.title.toLowerCase()}`}
      >
        <a
          href={item.url}
          onClick={(e) => {
            e.preventDefault();
            navigateClient(item.clientPath);
          }}
          data-testid={`client-link-${item.title.toLowerCase()}`}
        >
          <item.icon />
          <span data-testid={`menu-text-${item.title.toLowerCase()}`}>
            {item.title}
          </span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export const StandaloneSidebarClientMenuItems = ({
  disabled,
}: {
  disabled: boolean;
}) => {
  const clientMenuItems = useClientMenuItems();

  return (
    <>
      {clientMenuItems.map((item) => {
        const hasSubmenu = !!item.submenu && item.submenu.length > 0;
        return hasSubmenu ? (
          <StandaloneSidebarClientMenuItemWithSubmenu
            key={item.title}
            item={item}
            disabled={disabled}
          />
        ) : (
          <StandaloneSidebarClientMenuItem
            key={item.title}
            item={item}
            disabled={disabled}
          />
        );
      })}
    </>
  );
};
